/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Rocket, Shield, Brain, BarChart, Zap, Globe, CheckCircle } from 'lucide-react';
import { SurfaceCodeDiagram, AiBrainDiagram, SecurityDiagram } from './Diagrams';

const FeatureListItem = ({ icon, title, text }: { icon: React.ReactNode, title: string, text: string }) => (
    <div className="flex items-start gap-4">
        <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg text-stone-500 shrink-0 border border-stone-200 shadow-sm">
            {icon}
        </div>
        <div>
            <h4 className="font-bold text-stone-900">{title}</h4>
            <p className="text-sm text-stone-500">{text}</p>
        </div>
    </div>
);

export const Features: React.FC = () => {
    return (
        <section id="features" className="py-24 bg-white">
            <div className="container mx-auto px-6">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <span className="text-xs font-bold tracking-widest text-nobel-gold uppercase mb-2 block">Core Architecture</span>
                    <h2 className="font-serif text-4xl text-stone-900 mb-4">An Integrated Digital Asset Ecosystem</h2>
                    <p className="text-stone-500">A vertically integrated suite of tools designed for both sides of the market—from token creation to intelligent trading.</p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch max-w-6xl mx-auto">
                    {/* Card 1: For Creators */}
                    <div className="bg-gradient-to-br from-stone-50 to-white rounded-2xl border border-stone-200 p-8 flex flex-col h-full hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 effect-frame">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="p-3 bg-stone-900 text-white rounded-xl shadow-md">
                                <Rocket size={24} />
                            </div>
                            <div>
                                <h3 className="font-serif text-2xl font-bold text-stone-900">The Launchpad</h3>
                                <p className="text-sm text-stone-500 font-medium">For Token Creators & Innovators</p>
                            </div>
                        </div>
                        <div className="mb-6">
                            <SecurityDiagram />
                        </div>
                        <div className="space-y-6 mt-auto">
                           <FeatureListItem icon={<Globe size={18} />} title="Multi-Chain Deployment" text="Natively launch on Solana and Sui with one unified interface." />
                           <FeatureListItem icon={<Zap size={18} />} title="No-Code Extensions" text="Implement advanced features like transfer tax and immutable metadata with a single click." />
                           <FeatureListItem icon={<CheckCircle size={18} />} title="Instant Liquidity" text="Go live immediately on a bonding curve, ensuring fair launch and bot resistance." />
                        </div>
                    </div>

                    {/* Card 2: For Traders */}
                    <div className="bg-gradient-to-br from-stone-900 via-black to-stone-900 text-white rounded-2xl p-8 flex flex-col h-full hover:shadow-2xl hover:-translate-y-1 transition-all duration-300 effect-frame">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="p-3 bg-nobel-gold text-stone-900 rounded-xl shadow-md">
                                <BarChart size={24} />
                            </div>
                             <div>
                                <h3 className="font-serif text-2xl font-bold text-white">The Terminal</h3>
                                <p className="text-sm text-stone-400 font-medium">For Traders & Analysts</p>
                            </div>
                        </div>
                        <div className="mb-6">
                            <AiBrainDiagram />
                        </div>
                         <div className="space-y-6 mt-auto">
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-lg text-nobel-gold shrink-0 border border-white/10 shadow-sm">
                                    <Brain size={18} />
                                </div>
                                <div>
                                    <h4 className="font-bold text-white">AI-Powered Signals</h4>
                                    <p className="text-sm text-stone-400">Leverage swarm intelligence to detect market trends and smart money movements.</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-lg text-nobel-gold shrink-0 border border-white/10 shadow-sm">
                                    <Zap size={18} />
                                </div>
                                <div>
                                    <h4 className="font-bold text-white">High-Speed Execution</h4>
                                    <p className="text-sm text-stone-400">Trade through optimized routes via Jupiter and our native bonding curve for minimal slippage.</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-lg text-nobel-gold shrink-0 border border-white/10 shadow-sm">
                                    <Shield size={18} />
                                </div>
                                <div>
                                    <h4 className="font-bold text-white">Predictive Risk Analysis</h4>
                                    <p className="text-sm text-stone-400">Scan new tokens for potential risks like unlocked liquidity or active mint authority.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
